import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Button,
  TableBody,
  Typography,
  Box,
  IconButton,
  TextField,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  TableContainer,
  TablePagination,
  InputAdornment,
  Chip,
  Checkbox,
  ListItemText,
  Popover,
  List,
  ListItem,
  ListItemButton,
  Divider,
  TableSortLabel
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const CartaPorteTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Datos / estado
  const [data, setData] = useState([]);                 // filas actuales (página actual)
  const [total, setTotal] = useState(0);                // total en servidor
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Paginación y orden
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('fecha_actualizacion'); // default coherente con backend

  // Búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    rfcs: [],
    partners: [],
    fechaDesde: '',
    fechaHasta: ''
  });

  // UI auxiliares
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  // Catálogos / usuario
  const [usersData, setUsersData] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Popover de filtros
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');

  const open = Boolean(anchorEl);
  const popoverId = open ? 'filter-popover' : undefined;

  const isProveedor = currentUser?.roles?.includes('Proveedor');

  // Helpers de mapeo partner <-> RFC/nombre
  const getUserNameByPartner = (partnerId) => {
    if (!partnerId || !usersData.length) return '';
    const cleanPartner = String(partnerId).replace(/^0+/, '');
    const user = usersData.find(u =>
      u.partner && String(u.partner).replace(/^0+/, '') === cleanPartner
    );
    return user ? (user.nombre || '') : '';
  };

  const getRfcByPartner = (partnerId) => {
    if (!partnerId || !usersData.length) return '';
    const cleanPartner = String(partnerId).replace(/^0+/, '');
    const user = usersData.find(usr =>
      usr.partner && String(usr.partner).replace(/^0+/, '') === cleanPartner
    );
    // En tu sistema, username suele ser RFC
    return user ? (usrSafe(user.username) || user.rfc || '') : '';
  };

  const usrSafe = (v) => (v === null || v === undefined) ? '' : String(v);

  const getPartnerByRfc = (rfc) => {
    if (!rfc || !usersData.length) return '';
    const user = usersData.find(u => (u.rfc || u.username) === rfc);
    return user ? String(user.partner || '') : '';
  };

  // Opciones únicas para popover (derivadas de la página actual para performance)
  const getUniqueRfcsInData = () => {
    const rfcsInData = data
      .map(item => getRfcByPartner(item.partner_id))
      .filter(Boolean);
    return [...new Set(rfcsInData)];
  };

  const getUniquePartnersInData = () => {
    return [...new Set(data.map(item => String(item.partner_id)))];
  };

  // Sorting: mapeo campos front -> backend
  const sortFieldMap = {
    id_transporte: 'id_transporte',
    fecha_plan_trans: 'fecha_plan_trans',
    fecha_actualizacion: 'fecha_actualizacion',
    partner_id: 'partner_id',
    // 'rfc' y 'nombre' no existen directos en DB; los ordenamos localmente en la página
  };

  const handleRequestSort = (property) => {
    // Si ordenas por rfc/nombre haremos sort en cliente (página actual)
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Carga inicial: usuario + usuarios + primera página
  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        const [userResp, usersResp] = await Promise.all([
          axios.get('https://facturas.verdevalle.com.mx/api/validate-session', { withCredentials: true }),
          axios.get('https://facturas.verdevalle.com.mx/api/usuarios', { withCredentials: true })
        ]);
        setCurrentUser(userResp.data.user);
        setUsersData(usersResp.data);
      } catch (err) {
        console.error('Error al cargar usuario/usuarios:', err);
        setError('No se pudo validar la sesión o cargar usuarios.');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  // Fetch de datos cuando cambian filtros/paginación/búsqueda/orden
  useEffect(() => {
    const fetchFiltered = async () => {
      if (!currentUser) return; // espera a tener usuario
      try {
        setLoading(true);

        // Derivar partners desde RFCs seleccionados
        let partnersSet = new Set(filters.partners.map(p => String(p)));
        if (!isProveedor && filters.rfcs?.length) {
          filters.rfcs.forEach(rfc => {
            const p = getPartnerByRfc(rfc);
            if (p) partnersSet.add(String(p).replace(/^0+/, ''));
          });
        }
        const partnersParam = !isProveedor && partnersSet.size > 0
          ? Array.from(partnersSet).join(',')
          : undefined;

        // Mapear sort: si el campo no existe en backend, ordenaremos en cliente
        const canSortInServer = !!sortFieldMap[orderBy];
        const sortField = canSortInServer ? sortFieldMap[orderBy] : 'fecha_actualizacion';
        const sortOrder = order === 'asc' ? 'ASC' : 'DESC';

        const params = {
          page: page + 1,
          pageSize: rowsPerPage,
          q: searchTerm || undefined,
          from: filters.fechaDesde || undefined,
          to: filters.fechaHasta || undefined,
          partners: partnersParam,
          sortField,
          sortOrder
        };

        const { data: resp } = await axios.get('https://facturas.verdevalle.com.mx/api/transportes', {
          withCredentials: true,
          params
        });

        const rows = resp?.rows || [];
        setData(rows);
        setTotal(resp?.total || 0);
        // limpiar selección si cambió la página/filtros
        setSelectedRows([]);
        setRowSelectionModel([]);
      } catch (err) {
        console.error('Error al cargar transportes:', err);
        setError('Error al cargar los datos de cartas porte. Intente nuevamente.');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchFiltered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, searchTerm, filters.fechaDesde, filters.fechaHasta, filters.rfcs, filters.partners, page, rowsPerPage, order, orderBy]);

  // Ordenamiento local SOLO cuando se elige rfc o nombre (no existen en backend)
  const pageRows = useMemo(() => {
    if (orderBy === 'rfc' || orderBy === 'nombre') {
      const copy = [...data];
      copy.sort((a, b) => {
        const valA = orderBy === 'rfc'
          ? (getRfcByPartner(a.partner_id) || '').toLowerCase()
          : (getUserNameByPartner(a.partner_id) || '').toLowerCase();
        const valB = orderBy === 'rfc'
          ? (getRfcByPartner(b.partner_id) || '').toLowerCase()
          : (getUserNameByPartner(b.partner_id) || '').toLowerCase();
        if (valA < valB) return order === 'asc' ? -1 : 1;
        if (valA > valB) return order === 'asc' ? 1 : -1;
        return 0;
      });
      return copy;
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, orderBy, order, usersData]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const verXML = (idTransporte) => {
    try {
      const url = `https://facturas.verdevalle.com.mx/api/carta-porte/${idTransporte}/xml`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al abrir XML:', error);
      setError('Error al visualizar el XML');
      setSnackbarOpen(true);
    }
  };

  const verPDF = (idTransporte) => {
    try {
      const url = `https://facturas.verdevalle.com.mx/api/carta-porte/${idTransporte}/pdf`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al abrir PDF:', error);
      setError('Error al visualizar el PDF');
      setSnackbarOpen(true);
    }
  };

  const handleBulkDownload = async (type) => {
    try {
      if (selectedRows.length === 0) {
        setError('Selecciona al menos un registro para descargar');
        setSnackbarOpen(true);
        return;
      }
      const url = `https://facturas.verdevalle.com.mx/api/carta-porte/download-${type}?ids=${selectedRows.join(',')}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al descargar archivos:', error);
      setError('Error al generar archivo de descarga. Por favor intente nuevamente.');
      setSnackbarOpen(true);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      rfcs: [],
      partners: [],
      fechaDesde: '',
      fechaHasta: ''
    });
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = pageRows.map((row) => row.id_transporte);
      setSelectedRows(newSelected);
      setRowSelectionModel(newSelected);
      return;
    }
    setSelectedRows([]);
    setRowSelectionModel([]);
  };

  const handleRowClick = (event, idTransporte) => {
    const selectedIndex = selectedRows.indexOf(idTransporte);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, idTransporte);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }

    setSelectedRows(newSelected);
    setRowSelectionModel(newSelected);
  };

  const isSelected = (idTransporte) => selectedRows.indexOf(idTransporte) !== -1;

  const handleFilterClick = (event, filterType) => {
    setCurrentFilter(filterType);
    setAnchorEl(event.currentTarget);
    setFilterSearch('');
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
    setCurrentFilter(null);
    setFilterSearch('');
  };

  const handleToggleFilterItem = (value) => {
    const filterKey = currentFilter === 'rfc' ? 'rfcs' : 'partners';
    const currentFilters = [...filters[filterKey]];
    const currentIndex = currentFilters.indexOf(value);

    if (currentIndex === -1) {
      currentFilters.push(value);
    } else {
      currentFilters.splice(currentIndex, 1);
    }

    setFilters({
      ...filters,
      [filterKey]: currentFilters
    });
    setPage(0);
  };

  const handleSelectAllFilterItems = () => {
    const filterKey = currentFilter === 'rfc' ? 'rfcs' : 'partners';
    const allValues = currentFilter === 'rfc'
      ? getUniqueRfcsInData()
      : getUniquePartnersInData().map(String);

    setFilters({
      ...filters,
      [filterKey]: filters[filterKey].length === allValues.length ? [] : allValues
    });
    setPage(0);
  };

  const getFilteredOptions = () => {
    if (currentFilter === 'rfc') {
      return getUniqueRfcsInData().filter(rfc =>
        rfc.toLowerCase().includes(filterSearch.toLowerCase())
      );
    } else if (currentFilter === 'partner') {
      return getUniquePartnersInData()
        .map(String)
        .filter(partner =>
          partner.toLowerCase().includes(filterSearch.toLowerCase()) ||
          getUserNameByPartner(partner).toLowerCase().includes(filterSearch.toLowerCase())
        );
    }
    return [];
  };

  return (
    <Box sx={{
      p: isMobile ? 1 : 2,
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        mb: 2,
        gap: 1
      }}>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold' }}>
          Registros de Carta Porte
        </Typography>

        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{
            width: isMobile ? '100%' : 'auto',
            fontSize: '0.8rem'
          }}
        >
          Volver a factura
        </Button>
      </Box>

      <Paper elevation={1} sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 3,
        backgroundColor: '#fff',
        boxShadow: '0 10px 24px rgba(2, 6, 23, 0.06)',
        border: '1px solid #eef2f7'
      }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={5} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Buscar"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => { setSearchTerm(''); setPage(0); }}
                    sx={{ mr: -1 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
                sx: { height: '35px' }
              }}
              sx={{
                '& .MuiOutlinedInput-root': { height: '35px' },
                '& .MuiInputLabel-outlined': {
                  transform: 'translate(14px, 14px) scale(1)'
                },
                '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)'
                }
              }}
            />
          </Grid>

          {/* Ocultar filtros de RFC y Partner para usuarios Proveedor */}
          {!isProveedor && (
            <>
              <Grid item xs={6} sm={3} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={(e) => handleFilterClick(e, 'rfc')}
                  sx={{
                    height: '35px',
                    justifyContent: 'space-between',
                    textTransform: 'none'
                  }}
                  endIcon={<FilterListIcon fontSize="small" />}
                >
                  RFC Emisor
                  {filters.rfcs.length > 0 && (
                    <Chip label={filters.rfcs.length} size="small" sx={{ ml: 1 }} />
                  )}
                </Button>
              </Grid>

              <Grid item xs={6} sm={3} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={(e) => handleFilterClick(e, 'partner')}
                  sx={{
                    height: '35px',
                    justifyContent: 'space-between',
                    textTransform: 'none'
                  }}
                  endIcon={<FilterListIcon fontSize="small" />}
                >
                  Partner
                  {filters.partners.length > 0 && (
                    <Chip label={filters.partners.length} size="small" sx={{ ml: 1 }} />
                  )}
                </Button>
              </Grid>
            </>
          )}

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Fecha desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.fechaDesde}
              onChange={(e) => { setFilters({ ...filters, fechaDesde: e.target.value }); setPage(0); }}
              sx={{
                height: '35px',
                '& .MuiInputBase-root': { height: '35px' },
                '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '8.5px 14px' }
              }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Fecha hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.fechaHasta}
              onChange={(e) => { setFilters({ ...filters, fechaHasta: e.target.value }); setPage(0); }}
              sx={{
                height: '35px',
                '& .MuiInputBase-root': { height: '35px' },
                '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '8.5px 14px' }
              }}
            />
          </Grid>

          <Grid item xs={6} sm={2} md={1}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="error"
              onClick={handleClearFilters}
              startIcon={<ClearIcon fontSize="small" />}
              sx={{ height: '35px', fontSize: '0.7rem' }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 300, maxHeight: 400, p: 1 } }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={`Buscar ${currentFilter === 'rfc' ? 'RFC' : 'Partner'}...`}
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        <List dense sx={{ width: '100%', maxHeight: 300, overflow: 'auto' }}>
          {!isProveedor && (
            <>
              <ListItemButton onClick={handleSelectAllFilterItems}>
                <Checkbox
                  edge="start"
                  checked={
                    currentFilter === 'rfc'
                      ? filters.rfcs.length === getUniqueRfcsInData().length
                      : filters.partners.length === getUniquePartnersInData().length
                  }
                  indeterminate={
                    currentFilter === 'rfc'
                      ? filters.rfcs.length > 0 && filters.rfcs.length < getUniqueRfcsInData().length
                      : filters.partners.length > 0 && filters.partners.length < getUniquePartnersInData().length
                  }
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary="Seleccionar todos" />
              </ListItemButton>

              <Divider />
            </>
          )}

          {getFilteredOptions().map((value) => {
            const labelId = `checkbox-list-label-${value}`;
            const selected = currentFilter === 'rfc'
              ? filters.rfcs.includes(value)
              : filters.partners.includes(value);

            return (
              <ListItem key={value} disablePadding>
                <ListItemButton role={undefined} onClick={() => handleToggleFilterItem(value)} dense>
                  <Checkbox
                    edge="start"
                    checked={selected}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                  <ListItemText
                    id={labelId}
                    primary={value}
                    secondary={currentFilter === 'partner' ? getUserNameByPartner(value) : null}
                    sx={{
                      '& .MuiListItemText-secondary': {
                        fontSize: '0.75rem',
                        color: 'text.secondary'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Popover>

      {selectedRows.length > 0 && (
        <Box sx={{
          display: 'flex',
          gap: 1,
          mb: 2,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DescriptionIcon />}
            onClick={() => handleBulkDownload('xml')}
            disabled={loading}
            sx={{ fontSize: '0.75rem' }}
          >
            Descargar XML ({selectedRows.length})
          </Button>

          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => handleBulkDownload('pdf')}
            disabled={loading}
            sx={{ fontSize: '0.75rem' }}
          >
            Descargar PDF ({selectedRows.length})
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : data.length === 0 ? (
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2">
            {searchTerm || filters.rfcs.length || filters.partners.length || filters.fechaDesde || filters.fechaHasta
              ? 'No se encontraron resultados'
              : 'No hay registros disponibles'}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={1}
            sx={{
              maxHeight: '500px',
              overflow: 'auto',
              '&::-webkit-scrollbar': { width: '6px', height: '6px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '3px' }
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: '#f3f4f6',
                  '& th': {
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    py: 1,
                    borderBottom: '1px solid #bdbdbd'
                  }
                }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={selectedRows.length > 0 && selectedRows.length < pageRows.length}
                      checked={pageRows.length > 0 && selectedRows.length === pageRows.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'id_transporte'}
                      direction={orderBy === 'id_transporte' ? order : 'asc'}
                      onClick={() => handleRequestSort('id_transporte')}
                    >
                      Transporte
                    </TableSortLabel>
                  </TableCell>

                  {!isMobile && (
                    <>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'rfc'}
                          direction={orderBy === 'rfc' ? order : 'asc'}
                          onClick={() => handleRequestSort('rfc')}
                        >
                          RFC Emisor
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'partner_id'}
                          direction={orderBy === 'partner_id' ? order : 'asc'}
                          onClick={() => handleRequestSort('partner_id')}
                        >
                          Partner
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'nombre'}
                          direction={orderBy === 'nombre' ? order : 'asc'}
                          onClick={() => handleRequestSort('nombre')}
                        >
                          Nombre
                        </TableSortLabel>
                      </TableCell>
                    </>
                  )}

                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'fecha_plan_trans'}
                      direction={orderBy === 'fecha_plan_trans' ? order : 'asc'}
                      onClick={() => handleRequestSort('fecha_plan_trans')}
                    >
                      Fecha Plan
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pageRows.map((row) => {
                  const isItemSelected = isSelected(row.id_transporte);
                  return (
                    <TableRow
                      key={row.id_transporte}
                      hover
                      onClick={(event) => handleRowClick(event, row.id_transporte)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      selected={isItemSelected}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        '& td': { fontSize: '0.75rem', py: 0.5 }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox color="primary" checked={isItemSelected} />
                      </TableCell>

                      <TableCell>VVCP{row.id_transporte}</TableCell>

                      {!isMobile && (
                        <>
                          <TableCell>
                            <Chip
                              label={getRfcByPartner(row.partner_id)}
                              size="small"
                              sx={{ fontSize: '0.7rem', height: '24px' }}
                            />
                          </TableCell>
                          <TableCell>{row.partner_id}</TableCell>
                          <TableCell>{getUserNameByPartner(row.partner_id)}</TableCell>
                        </>
                      )}

                      <TableCell>
                        {row.fecha_plan_trans
                          ? new Date(row.fecha_plan_trans).toLocaleDateString('es-MX', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })
                          : ''}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); verXML(row.id_transporte); }}
                            aria-label="Ver XML"
                            color="primary"
                            sx={{ p: 0.5, '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' } }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); verPDF(row.id_transporte); }}
                            aria-label="Ver PDF"
                            color="error"
                            sx={{ p: 0.5, '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' } }}
                          >
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              mt: 1,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.75rem'
              }
            }}
          />
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%', fontSize: '0.8rem' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CartaPorteTable;
